import {
  Alert,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertIcon,
  Button,
  ButtonGroup,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Icon,
  Stack,
  Text,
  Textarea,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
  FiBriefcase,
  FiCalendar,
  FiCheckCircle,
  FiCornerUpLeft,
  FiExternalLink,
  FiLayers,
  FiMessageSquare,
  FiXCircle,
} from "react-icons/fi";
import { useAppDispatch } from "../../common/redux/hooks";
import { useNavigate } from "react-router-dom";
import {
  NeedStatus,
  useCreateMarketDialogueForNeedMutation,
  useCreateProcurementForNeedMutation,
  useListFrameworkAgreementsQuery,
  useUpdateNeedMutation,
  type NeedDto,
  type NeedPatchDto,
  type PlannedStartDto,
} from "../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../common/auth/useLoggedInWithOrgContextState";
import { useEditor } from "../../common/editing/save-coordinator";
import { useApiError } from "../../common/errors/useApiError";
import { useCreateProcurementWithAi } from "../../common/agent/useCreateProcurementWithAi";
import { useAppRole } from "../../common/layout/useAppRole";
import { HELD_BY_PROCUREMENT } from "../../common/needs/useNeedStatusLabel";
import { urls } from "../../urls";
import { procurementEntryHref } from "../sourcing-event/procurementEntryHref";
import { isNeedAlreadyStarted, reloadNeedIfStale } from "./staleNeed";
import { CreateProcurementFromNeedDialog } from "./CreateProcurementFromNeedDialog";
import { takenByName } from "./needFieldErrors";
import { PlanNeedDialog } from "./PlanNeedDialog";
import { StartFromFrameworkAgreementDialog } from "./StartFromFrameworkAgreementDialog";
import { startableAgreements } from "./startFromFrameworkAgreement";
import { WithdrawNeedButton } from "./WithdrawNeedButton";

interface Props {
  needId: string;
}

export const NeedActions = ({ needId }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { me } = useLoggedInWithOrgContextState();
  const { state, updateField, fieldStatus } = useEditor<NeedDto, NeedPatchDto>(needId);
  const apiError = useApiError();
  const role = useAppRole();
  const isAdvisor = role === "advisor";
  const isCreator = state.createdBy.id === me.id;

  const reject = useDisclosure();
  const plan = useDisclosure();
  const sendBack = useDisclosure();
  const agreementDialog = useDisclosure();
  const attachmentsDialog = useDisclosure();
  const attachments = state.documents ?? [];

  const [createProcurement, { isLoading: isCreatingProcurement }] = useCreateProcurementForNeedMutation();
  const [createMarketDialogue, { isLoading: isStartingMarketDialogue }] = useCreateMarketDialogueForNeedMutation();
  const createWithAi = useCreateProcurementWithAi();
  const resumeInterview = state.procurement?.unsavedInterview ? createWithAi : null;
  const isStatusSaving = fieldStatus("status") === "saving" || fieldStatus("status") === "pending";

  const [decide] = useUpdateNeedMutation();
  const toast = useToast();
  const [isAccepting, setAccepting] = useState(false);
  const onTake = async () => {
    setAccepting(true);
    try {
      await decide({ needId, needPatchDto: { status: NeedStatus.Approved } }).unwrap();
    } catch (e) {
      const takenBy = takenByName(e);
      toast(
        takenBy
          ? { status: "info", title: t("{{name}} has already taken the need.", { name: takenBy }), isClosable: true }
          : { status: "error", title: takeFailureMessage(e, t), isClosable: true },
      );
    } finally {
      setAccepting(false);
    }
  };
  const onReject = async (reason: string) => {
    await decide({ needId, needPatchDto: { status: NeedStatus.Rejected, decisionComment: reason } }).unwrap();
  };
  const onSendBack = async (comment: string) => {
    await decide({ needId, needPatchDto: { status: NeedStatus.SentBack, decisionComment: comment } }).unwrap();
  };
  const onPlan = async (plannedStart: PlannedStartDto) => {
    await decide({ needId, needPatchDto: { plannedStart } }).unwrap();
  };
  const onCreateProcurement = async (attachmentIds: string[]) => {
    try {
      const updated = await createProcurement({
        needId,
        createProcurementForNeedRequest: { attachmentIds },
      }).unwrap();
      attachmentsDialog.onClose();
      if (updated.procurement) navigate(procurementEntryHref(updated.procurement.status, updated.procurement.id));
    } catch (e) {
      apiError.trigger(e);
      reloadNeedIfStale(e, dispatch);
      if (isNeedAlreadyStarted(e)) attachmentsDialog.onClose();
    }
  };

  const onStartMarketDialogue = async () => {
    try {
      const dialogue = await createMarketDialogue({ needId }).unwrap();
      navigate(urls.app.marketDialogues.view.go(dialogue.id));
    } catch (e) {
      apiError.trigger(e);
    }
  };

  const canTake = !!state.canTake;
  const canReject = state.status === NeedStatus.Reported && isAdvisor;
  const canSendBack = state.status === NeedStatus.Reported && isAdvisor;
  const canWithdraw = (state.status === NeedStatus.Reported || state.status === NeedStatus.SentBack) && isCreator;
  const canCreateProcurement = HELD_BY_PROCUREMENT.includes(state.status) && isAdvisor;
  const {
    data: frameworkAgreements,
    isError: agreementsFailed,
    refetch: refetchAgreements,
  } = useListFrameworkAgreementsQuery({}, { skip: !canCreateProcurement });
  const agreements = startableAgreements(t, frameworkAgreements?.frameworkAgreements ?? []);
  const canUseAgreement = canCreateProcurement && agreements.length > 0;
  const canPlan = !!state.canPlan;
  const canStartMarketDialogue = !!state.canStartMarketDialogue && isAdvisor;
  const hasAnyAction =
    canTake || canReject || canSendBack || canWithdraw || canCreateProcurement || canPlan || canStartMarketDialogue;

  if (!hasAnyAction && !state.procurement && !state.callOff) return null;

  return (
    <>
      <Stack
        spacing="3"
        bg="smBackground"
        border="1px solid"
        borderColor="smBorder"
        rounded="xl"
        p="4"
      >
        {state.procurement && (
          <Flex justify="space-between" align="center" gap="3" wrap="wrap">
            <Text fontSize="sm" color="smText">
              <Text as="span" color="smMuted">
                {state.status === NeedStatus.UnderCallOff ? t("Mini-competition created") : t("Procurement created")}:
              </Text>{" "}
              <Text as="span" fontWeight="medium">
                {state.procurement.title?.trim() || t("Untitled")}
              </Text>
            </Text>
            <Button
              size="sm"
              variant="outline"
              colorScheme="teal"
              leftIcon={resumeInterview ? <Icon as={FiMessageSquare} /> : undefined}
              rightIcon={resumeInterview ? undefined : <Icon as={FiExternalLink} />}
              onClick={() =>
                resumeInterview
                  ? resumeInterview()
                  : state.procurement && navigate(procurementEntryHref(state.procurement.status, state.procurement.id))
              }
            >
              {resumeInterview ? t("Continue with AI") : t("Open procurement")}
            </Button>
          </Flex>
        )}

        {state.callOff && (
          <Flex justify="space-between" align="center" gap="3" wrap="wrap">
            <Text fontSize="sm" color="smText">
              <Text as="span" color="smMuted">
                {t("Call-off created")}:
              </Text>{" "}
              <Text as="span" fontWeight="medium">
                {state.callOff.title?.trim() || t("Untitled")}
              </Text>
            </Text>
            <Button
              size="sm"
              variant="outline"
              colorScheme="teal"
              rightIcon={<Icon as={FiExternalLink} />}
              onClick={() => state.callOff && navigate(urls.app.contracts.view.go(state.callOff.id))}
            >
              {t("Open call-off")}
            </Button>
          </Flex>
        )}

        {hasAnyAction && (
          <ButtonGroup spacing="3" flexWrap="wrap" rowGap="2">
            {canTake && (
              <Button
                colorScheme="teal"
                leftIcon={<Icon as={FiCheckCircle} />}
                onClick={() => void onTake()}
                isLoading={isAccepting}
              >
                {t("Take the need")}
              </Button>
            )}
            {canSendBack && (
              <Button
                variant="outline"
                leftIcon={<Icon as={FiCornerUpLeft} />}
                onClick={sendBack.onOpen}
                isDisabled={isStatusSaving || isAccepting}
              >
                {t("Send back")}
              </Button>
            )}
            {canReject && (
              <Button
                colorScheme="red"
                variant="outline"
                leftIcon={<Icon as={FiXCircle} />}
                onClick={reject.onOpen}
                isDisabled={isStatusSaving || isAccepting}
              >
                {t("Reject need")}
              </Button>
            )}
            {canWithdraw && (
              <WithdrawNeedButton
                onWithdraw={() => updateField({ status: NeedStatus.Withdrawn })}
                isDisabled={isStatusSaving || isAccepting}
              />
            )}
            {canPlan && (
              <Button variant="outline" leftIcon={<Icon as={FiCalendar} />} onClick={plan.onOpen}>
                {state.plannedStart ? t("Change planned start") : t("Plan")}
              </Button>
            )}
            {canCreateProcurement && (
              <Button
                colorScheme="teal"
                leftIcon={<Icon as={FiBriefcase} />}
                onClick={() => (attachments.length > 0 ? attachmentsDialog.onOpen() : void onCreateProcurement([]))}
                isLoading={isCreatingProcurement && !attachmentsDialog.isOpen}
              >
                {t("Create procurement")}
              </Button>
            )}
            {canStartMarketDialogue && (
              <Button
                variant="outline"
                leftIcon={<Icon as={FiMessageSquare} />}
                onClick={() => void onStartMarketDialogue()}
                isLoading={isStartingMarketDialogue}
              >
                {t("Start market dialogue")}
              </Button>
            )}
            {canUseAgreement && (
              <Button variant="outline" leftIcon={<Icon as={FiLayers} />} onClick={agreementDialog.onOpen}>
                {t("Use a framework agreement")}
              </Button>
            )}
          </ButtonGroup>
        )}
        {canCreateProcurement && agreementsFailed && (
          <Text fontSize="sm" color="smMuted">
            {t("The framework agreements could not be loaded, so you can't see whether one can be used.")}{" "}
            <Button variant="link" size="sm" onClick={() => void refetchAgreements()}>
              {t("Try again")}
            </Button>
          </Text>
        )}
      </Stack>

      {attachmentsDialog.isOpen && (
        <CreateProcurementFromNeedDialog
          attachments={attachments}
          isCreating={isCreatingProcurement}
          onCreate={onCreateProcurement}
          onClose={attachmentsDialog.onClose}
        />
      )}

      {agreementDialog.isOpen && (
        <StartFromFrameworkAgreementDialog need={state} agreements={agreements} onClose={agreementDialog.onClose} />
      )}

      {plan.isOpen && (
        <PlanNeedDialog
          isOpen
          onClose={plan.onClose}
          current={state.plannedStart}
          currentHasPassed={state.plannedStartHasPassed}
          onConfirm={onPlan}
          failureMessage={(e) => planFailureMessage(e, t)}
        />
      )}

      <DecisionDialog
        isOpen={reject.isOpen}
        onClose={reject.onClose}
        onConfirm={onReject}
        title={t("Reject this need?")}
        description={t("This is final and cannot be undone.")}
        label={t("Reason for rejecting")}
        helper={t("The reporter reads this on the need.")}
        confirmLabel={t("Reject")}
        confirmColorScheme="red"
      />

      <DecisionDialog
        isOpen={sendBack.isOpen}
        onClose={sendBack.onClose}
        onConfirm={onSendBack}
        title={t("Send this need back to the reporter?")}
        description={t("The reporter can correct the need and report it again.")}
        label={t("What should be corrected")}
        helper={t("The reporter reads this on the need.")}
        confirmLabel={t("Send back")}
        confirmColorScheme="teal"
      />
    </>
  );
};

const DecisionDialog = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  label,
  helper,
  confirmLabel,
  confirmColorScheme,
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (text: string) => Promise<void>;
  title: string;
  description: string;
  label: string;
  helper: string;
  confirmLabel: string;
  confirmColorScheme: string;
}) => {
  const { t } = useTranslation();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [text, setText] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const close = () => {
    if (isSaving) return;
    setText("");
    setFailure(null);
    onClose();
  };
  const confirm = async () => {
    setIsSaving(true);
    setFailure(null);
    try {
      await onConfirm(text.trim());
      setText("");
      onClose();
    } catch (e) {
      setFailure(decisionFailureMessage(e, t));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={close}>
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader>{title}</AlertDialogHeader>
          <AlertDialogBody>
            <Text mb="4">{description}</Text>
            <FormControl isRequired>
              <FormLabel fontSize="sm">{label}</FormLabel>
              <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} />
              <FormHelperText>{helper}</FormHelperText>
            </FormControl>
            {failure && (
              <Alert status="error" mt="4" rounded="md" fontSize="sm">
                <AlertIcon />
                {failure}
              </Alert>
            )}
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={close} variant="ghost" isDisabled={isSaving}>
              {t("Cancel")}
            </Button>
            <Button
              colorScheme={confirmColorScheme}
              ml="3"
              isDisabled={text.trim() === ""}
              isLoading={isSaving}
              onClick={() => void confirm()}
            >
              {confirmLabel}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};

const planFailureMessage = (error: unknown, t: TFunction): string => {
  if (JSON.stringify(error).includes("zz_FieldErrorCode_PlannedStartInPast")) {
    return t("That month has already passed.");
  }
  return (error as { status?: unknown })?.status === 403
    ? t("You are no longer responsible for this need. Reload the page.")
    : decisionFailureMessage(error, t);
};

const takeFailureMessage = (error: unknown, t: TFunction): string =>
  (error as { status?: unknown })?.status === 403
    ? t("You may not decide on needs in this department.")
    : decisionFailureMessage(error, t);

const decisionFailureMessage = (error: unknown, t: TFunction): string => {
  const status = (error as { status?: unknown })?.status;
  if (status === 403) return t("You may not decide on this need.");
  if (status === 400 && JSON.stringify(error).includes("zz_FieldErrorCode_ImmutableInStatus")) {
    return t("The need has already been handled. Reload the page.");
  }
  return t("Could not save. Try again.");
};
